import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { type AuditActionKind, type AuditEntry, fetchAudit, type FieldChange } from './api'

const ACTION_LABELS: Record<AuditActionKind, string> = {
  create: 'создано',
  update: 'изменено',
  delete: 'удалено',
}

const ENTITY_LABELS: Record<string, string> = {
  Department: 'Подразделение',
  DepartmentAlias: 'Псевдоним подразделения',
  Position: 'Должность',
  Employee: 'Сотрудник',
  EmployeeAlias: 'Вариант имени',
  EmployeeContact: 'Контакт',
  EmploymentPeriod: 'Период занятости',
  OrgLink: 'Связь',
  User: 'Аккаунт',
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'да' : 'нет'
  return String(value)
}

function formatTime(value: string): string {
  return new Date(value).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'medium' })
}

function Changes({ changes, action }: { changes: FieldChange[]; action: AuditActionKind }) {
  if (changes.length === 0) {
    return <span className="muted">без изменяемых полей</span>
  }

  return (
    <ul className="changes">
      {changes.map((change) => (
        <li key={change.field}>
          <code>{change.field}</code>{' '}
          {action === 'update' ? (
            <>
              <span className="muted">{formatValue(change.before)}</span>
              {' → '}
              <span>{formatValue(change.after)}</span>
            </>
          ) : (
            <span>{formatValue(action === 'delete' ? change.before : change.after)}</span>
          )}
        </li>
      ))}
    </ul>
  )
}

function Entry({ entry, onPickRequest }: { entry: AuditEntry; onPickRequest: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? entry.changes : entry.changes.slice(0, 3)
  const hidden = entry.changes.length - visible.length

  return (
    <tr>
      <td className="nowrap">{formatTime(entry.created_at)}</td>
      <td>{entry.actor_email ?? <span className="muted">система</span>}</td>
      <td>
        {ENTITY_LABELS[entry.entity_type] ?? entry.entity_type}
        {entry.entity_label && <div className="muted small">{entry.entity_label}</div>}
      </td>
      <td className="nowrap">{ACTION_LABELS[entry.action]}</td>
      <td>
        <Changes changes={visible} action={entry.action} />
        {hidden > 0 && (
          <button type="button" className="link" onClick={() => setExpanded(true)}>
            ещё {hidden}
          </button>
        )}
      </td>
      <td>
        <button
          type="button"
          className="link"
          title="Показать всё, что изменил этот же запрос"
          onClick={() => onPickRequest(entry.request_id)}
        >
          действие
        </button>
      </td>
    </tr>
  )
}

export default function AuditPage() {
  const [entityType, setEntityType] = useState('')
  const [requestId, setRequestId] = useState('')
  const [offset, setOffset] = useState(0)
  const limit = 50

  const audit = useQuery({
    queryKey: ['audit', entityType, requestId, offset],
    queryFn: () =>
      fetchAudit({
        entityType: entityType || undefined,
        requestId: requestId || undefined,
        limit,
        offset,
      }),
  })

  return (
    <section>
      <div className="card">
        <h2>Журнал изменений</h2>

        <div className="inline-fields">
          <label className="field">
            <span>Тип объекта</span>
            <select
              value={entityType}
              onChange={(event) => {
                setEntityType(event.target.value)
                setOffset(0)
              }}
            >
              <option value="">все</option>
              {(audit.data?.entity_types ?? []).map((type) => (
                <option key={type} value={type}>
                  {ENTITY_LABELS[type] ?? type}
                </option>
              ))}
            </select>
          </label>

          {requestId && (
            <label className="field">
              <span>Одно действие</span>
              <button
                type="button"
                onClick={() => {
                  setRequestId('')
                  setOffset(0)
                }}
              >
                Сбросить фильтр
              </button>
            </label>
          )}
        </div>

        {audit.isPending && <p>Загружаем…</p>}
        {audit.isError && <p className="bad">Не удалось загрузить журнал</p>}

        {audit.data && (
          <>
            <table>
              <thead>
                <tr>
                  <th>Когда</th>
                  <th>Кто</th>
                  <th>Объект</th>
                  <th>Что</th>
                  <th>Изменения</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {audit.data.entries.map((entry) => (
                  <Entry
                    key={entry.id}
                    entry={entry}
                    onPickRequest={(id) => {
                      setRequestId(id)
                      setOffset(0)
                    }}
                  />
                ))}
              </tbody>
            </table>

            {audit.data.entries.length === 0 && <p className="muted">Записей нет.</p>}

            {audit.data.total > limit && (
              <div className="pager">
                <button type="button" disabled={offset === 0} onClick={() => setOffset(offset - limit)}>
                  Назад
                </button>
                <span className="muted">
                  {offset + 1}–{Math.min(offset + limit, audit.data.total)} из {audit.data.total}
                </span>
                <button
                  type="button"
                  disabled={offset + limit >= audit.data.total}
                  onClick={() => setOffset(offset + limit)}
                >
                  Вперёд
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}
